import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import FocusTrap from 'focus-trap-react'

// components
import Button from '/react4xp/components/Button'
import Guidelines from '/react4xp/components/Guidelines'
import Clock from '/react4xp/components/Clock'
import SearchInput from '/react4xp/components/SearchInput'

// utisl
import { tableWrapper } from '/lib/reactutils/utils'

interface HeaderProps {
    bgColor?: 'transparent' | 'primary' | 'white' | string;
    logoColor?: 'text-light' | 'text-dark' | string;
    isPrototype?: boolean;
    skipToMain: string;
    logoSvg: string;
    logoHref: string;
    logoAriaLabel: string;
    topMenu: Array<{ name: string, href: string, external?: boolean, icon?:string }>;
    searchHref: string;
    searchText: string;
    menuOpen: string;
    menuClose: string;
    menuFirstItems: Array<{
        name: string;
        link?: string;
        external?: boolean;
        subTopics: Array<{
            name: string;
            link: string;
            external?: boolean
        }>
    }>;
    menuSecondItems: Array<{ name: string, link: string, external?: boolean }>;
    clocks: Array<{ label: string, zone: string, background: string }>;
    custormerPortal: { name: string, link: string, external?: boolean };
    signIn?: { name: string, link: string, icon?: string };
}

export default function Header({
    bgColor,
    logoColor,
    isPrototype,
    skipToMain,
    logoSvg,
    logoHref,
    logoAriaLabel,
    topMenu,
    searchHref,
    searchText,
    menuOpen,
    menuClose,
    menuFirstItems,
    menuSecondItems,
    clocks,
    custormerPortal,
    signIn
}: HeaderProps) {

    // table wrapper function
    tableWrapper()

    const searchInputRef = useRef<HTMLInputElement>(null)
    const searchToggleRef = useRef<HTMLElement>(null)
    const menuToggleRef = useRef<HTMLElement>(null)
    const drawerRef = useRef<HTMLDivElement>(null)
    const [isMenuShown, setIsMenuShown] = useState(false)
    const [isSearchActive, setIsSearchActive] = useState(false)
    const [menuColor, setMenuColor] = useState(bgColor || '')
    const [scrollPosition, setScrollPosition] = useState(0)
    const [firstSectionBgColor, setFirstSectionBgColor] = useState('')
    const [isDarkColor, setIsDarkColor] = useState(false)

    // Focus the first candidate that can actually take it. A toggle hidden by CSS
    // (visibility: hidden) silently ignores focus(), which would strand focus on
    // <body> now that the trap no longer returns it on deactivate.
    const focusFirstAvailable = (...candidates: (HTMLElement | null)[]) => {
        for (let i = 0; i < candidates.length; i += 1) {
            const candidate = candidates[i]
            if (!candidate) {
                continue
            }
            candidate.focus()
            if (window.document.activeElement === candidate) {
                return
            }
        }
    }

    const closeMenu = () => {
        setIsMenuShown(false)
        window.document.body.style.overflowY = 'auto'
        focusFirstAvailable(menuToggleRef.current, searchToggleRef.current)
    }

    const closeSearch = () => {
        setIsSearchActive(false)
        window.document.body.style.overflowY = 'auto'
        // The search toggle stays visible in this variant at every width, so it
        // survives the render that closes the search and is the right target.
        // Compare with Header/SuccessStories.tsx, where it does not.
        focusFirstAvailable(searchToggleRef.current, menuToggleRef.current)
    }

    const openCloseMenu = () => {

        if (isMenuShown) {
            closeMenu()
            return
        }

        if (isSearchActive) {
            setIsSearchActive(false)
        }

        setScrollPosition(window.scrollY)
        setIsMenuShown(true)
        window.document.body.style.overflowY = 'scroll'
    }

    const openCloseSearch = (event: React.MouseEvent) => {
        event.preventDefault()

        if (isSearchActive) {
            closeSearch()
            return
        }

        // close the menu if it's open
        if (isMenuShown) {
            setIsMenuShown(false)
        }

        // save current scroll position
        setScrollPosition(window.scrollY)
        setIsSearchActive(true)
        window.document.body.style.overflowY = 'scroll'
    }

    // close the open panel on Escape and hand focus back to its trigger
    useEffect(() => {
        if (!isMenuShown && !isSearchActive) {
            return
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') {
                return
            }

            if (isMenuShown) {
                closeMenu()
            } else {
                closeSearch()
            }
        }

        window.document.addEventListener('keydown', handleKeyDown)

        return () => window.document.removeEventListener('keydown', handleKeyDown)
    }, [isMenuShown, isSearchActive])

    // focus the panel that is actually opening; closeMenu / closeSearch own the
    // return trip, so the trap must not fight them on deactivate.
    // focus-trap-react captures focusTrapOptions once, so this closure has to read
    // live state through a ref instead of the render-time value.
    const isSearchActiveRef = useRef(isSearchActive)
    isSearchActiveRef.current = isSearchActive

    const panelInitialFocus = () => isSearchActiveRef.current
        ? searchInputRef.current || false
        : drawerRef.current?.querySelector<HTMLElement>('a[href], button:not([disabled])') || false

    useEffect(() => {
        let lastScrollTop = 0
    
        const handleScroll = () => {
            if (!isMenuShown && !isSearchActive) {
                const menu = window.document.getElementById('j-menu')
                const header = window.document.querySelector('header')
    
                // show / hide header based on scroll direction
                const isMobile = window.matchMedia('(max-width: 767px)').matches

                let currentScroll = 0

                if (isMobile) {
                    currentScroll = window.pageYOffset || window.document.documentElement.scrollTop
                }
    
                // scrolling down
                if (currentScroll > lastScrollTop) {
                    header.classList.add('j-menu--hidden')
                // scrolling up
                } else {
                    header.classList.remove('j-menu--hidden')
                }
    
                lastScrollTop = currentScroll <= 0 ? 0 : currentScroll
    
                // manage j-menu class
                if (window.scrollY > 0) {
                    menu.classList.add('j-menu--expanded')
                } else {
                    menu.classList.remove('j-menu--expanded')
                }
    
                updateMenuColor()
            }
        }

        const menu = window.document.getElementById('j-menu')
        
        if (isMenuShown || isSearchActive) {
            menu.classList.remove('j-menu--expanded')
        } else if (window.scrollY > 0) {
            menu.classList.add('j-menu--expanded')
        } else {
            menu.classList.remove('j-menu--expanded')
        }

        const updateMenuColor = () => {
            const sections = window.document.querySelectorAll('section')
            let currentBgColor = ''

            sections.forEach(section => {
                const rect = section.getBoundingClientRect()
                const isMobile = window.matchMedia('(max-width: 767px)').matches
                const isTablet = window.matchMedia('(min-width: 768px)').matches
                const measureMobile = isMobile && 60
                const measureTablet = isTablet && 36

                if (rect.top <= measureMobile && rect.bottom >= 0) {
                    const sectionBgColor = window.getComputedStyle(section).backgroundColor

                    if (sectionBgColor !== 'rgba(0, 0, 0, 0)' && sectionBgColor !== 'transparent') {
                        currentBgColor = sectionBgColor
                    }
                }

                if (rect.top <= measureTablet && rect.bottom >= measureTablet) {
                    const sectionBgColor = window.getComputedStyle(section).backgroundColor

                    if (sectionBgColor !== 'rgba(0, 0, 0, 0)' && sectionBgColor !== 'transparent') {
                        currentBgColor = sectionBgColor;
                    }
                }

            })

            // if no valid section background color is found, check <main>
            if (!currentBgColor || currentBgColor === 'rgba(0, 0, 0, 0)' || currentBgColor === 'transparent') {
                const mainElement = window.document.getElementById('main')

                if (mainElement) {
                    const mainBgColor = window.getComputedStyle(mainElement).backgroundColor

                    if (mainBgColor !== 'rgba(0, 0, 0, 0)' && mainBgColor !== 'transparent') {
                        currentBgColor = mainBgColor
                    }
                }
            }

            // if no valid background is found in <main>, check <body>
            if (!currentBgColor || currentBgColor === 'rgba(0, 0, 0, 0)' || currentBgColor === 'transparent') {
                const bodyBgColor = window.getComputedStyle(document.body).backgroundColor

                currentBgColor = bodyBgColor !== 'rgba(0, 0, 0, 0)' && bodyBgColor !== 'transparent'
                    ? bodyBgColor
                    : 'transparent'
            }
        
            setMenuColor(currentBgColor)
        }

        window.addEventListener('scroll', handleScroll)
        updateMenuColor()

        return () => window.removeEventListener('scroll', handleScroll)
    }, [isMenuShown, isSearchActive])

    useEffect(() => {
        setFirstSectionBgColor(window.document.querySelector('section') ? window.getComputedStyle(window.document.querySelector('section')).backgroundColor : '')
        setIsDarkColor([
            '#001F42',
            '#002855',
            'rgb(0, 31, 66)'
        ].includes(firstSectionBgColor) || ['#001F42', '#002855'].includes(rgbToHex(firstSectionBgColor)))
        
        // prevent automatic scroll restoration
        history.scrollRestoration = 'manual'

        // restore default behavior on cleanup
        return () => {
            history.scrollRestoration = 'auto'
        }
    }, [])

    // focus the input field
    useEffect(() => {
        if (isSearchActive && searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [isSearchActive])

    const rgbToHex = (rgb: string) => {
        const rgbValues = rgb.match(/\d+/g)
        return rgbValues ? `#${rgbValues.map(x => parseInt(x).toString(16).padStart(2, '0')).join('')}`.toUpperCase() : ''
    }

    const isActiveColor = [
        '#001F42',
        '#002855',
        'rgb(0, 31, 66)'
    ].includes(menuColor) || ['#001F42', '#002855'].includes(rgbToHex(menuColor))

    return (
        <>
            {isPrototype && <Guidelines />}

            <Button
                href='#main'
                className='skip-to-main'
            >
                {skipToMain}
            </Button>

            <FocusTrap
                active={isMenuShown || isSearchActive}
                focusTrapOptions={{
                    escapeDeactivates: false,
                    clickOutsideDeactivates: false,
                    initialFocus: panelInitialFocus,
                    returnFocusOnDeactivate: false
                }}
            >

                <header id='j-menu' className='j-menu' style={menuColor ? { backgroundColor: menuColor } : null}>
                    <div className={clsx(
                        'grid-container relative j-menu__navbar',
                        (isMenuShown || isSearchActive) && 'j-menu__navbar--active',
                        isActiveColor && 'j-menu__navbar--light',
                        isDarkColor && 'j-menu__navbar--light',
                    )} style={isSearchActive ? { zIndex: 1001 } : null}>
                        <div className="grid-row">
                            <div className="c-12 j-menu__wrapper">
                                <a className={clsx('j-menu__logo')} href={logoHref} id='top-menu-logo' aria-label={logoAriaLabel}>
                                    <span
                                        dangerouslySetInnerHTML={{ __html: logoSvg }}
                                        suppressHydrationWarning
                                    />
                                </a>

                                <div className='j-menu__actions'>
                                    <ul>
                                        {topMenu.map((item, i) => (
                                            <li key={i} className={item.icon && "j-menu__actions-li-icon"}>
                                                <Button
                                                    href={item.href}
                                                    target={item.external}
                                                    menuBtn='pages'
                                                    className={clsx('text-dark labels-lg-regular hover-underline')}
                                                >
                                                    {item.name}
                                                    {item.icon && <span
                                                        className="j-menu__actions-icon"
                                                        dangerouslySetInnerHTML={{ __html: item.icon }}
                                                    />}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className='j-menu__icons'>
                                        {/* BUTTON SEARCH */}
                                        <Button
                                            ref={searchToggleRef}
                                            href={searchHref}
                                            rightIcon={isSearchActive ? 'Times' : 'Search'}
                                            className={clsx('j-menu__search-button', isSearchActive && 'active j-menu__closeSearch')}
                                            menuBtn='pages'
                                            onClick={openCloseSearch}
                                            ariaLabel={isSearchActive ? menuClose : searchText}
                                            ariaExpanded={isSearchActive}
                                            ariaControls='j-menu__search'
                                        />
                                        {/* BUTTON MENU  HAMBURGUER */}
                                        <Button
                                            ref={menuToggleRef}
                                            className={clsx('j-menu__hamburguer', isMenuShown && 'active j-menu__closeHamburguer')}
                                            rightIcon={isMenuShown ? 'Times' : 'Hamburguer'}
                                            menuBtn='pages'
                                            onClick={openCloseMenu}
                                            ariaLabel={isMenuShown ? menuClose : menuOpen}
                                            ariaExpanded={isMenuShown}
                                            ariaControls='j-menu__drawer'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    <div ref={drawerRef} id='j-menu__drawer' className={clsx('j-menu__drawer', isMenuShown ? 'j-menu__drawer--active bgcolor' : '')}>
                        <div className='grid-container'>
                            <div className='grid-row j-menu__inner-drawer'>
                                <div className="c-12 c-md-4 j-menu__items">
                                    {menuFirstItems.map((topic, i) => (
                                        <div key={i} className='j-menu__primaries'>

                                            {topic.link ? (
                                                <Button
                                                    href={topic.link}
                                                    target={topic.external}
                                                    menuBtn='topic'
                                                    className='hover-underline'
                                                >
                                                    {topic.name}
                                                </Button>
                                            ) : (
                                                <p className='btn btn__menu--topic'>
                                                    {topic.name}
                                                </p>
                                            )}

                                            {topic.subTopics.length > 0 && (
                                                <ul className='j-menu__sub-items'>
                                                    {topic.subTopics.map((subTopic, j) => (
                                                        <li key={j}>
                                                            <Button
                                                                href={subTopic.link}
                                                                target={subTopic.external}
                                                                menuBtn='sub-topic'
                                                                className='hover-underline'
                                                            >
                                                                {subTopic.name}
                                                            </Button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="c-xs-6 start-md-5 c-md-3 j-menu__items">
                                    <ul className='j-menu__secondary-items'>
                                        {menuSecondItems.slice(0, 4).map((item, i) => (
                                            <li key={i}>
                                                <Button
                                                    href={item.link}
                                                    target={item.external}
                                                    menuBtn='sub-topic'
                                                    className='hover-underline'
                                                >
                                                    {item.name}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="c-xs-6 c-md-3 j-menu__items">
                                    <ul className='j-menu__secondary-items'>
                                        {menuSecondItems.slice(4, 8).map((item, i) => (
                                            <li key={i}>
                                                <Button
                                                    href={item.link}
                                                    target={item.external}
                                                    menuBtn='sub-topic'
                                                    className='hover-underline'
                                                >
                                                    {item.name}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                {
                                    (custormerPortal.name || signIn?.name) &&
                                    <div className="c-12 c-md-2 j-menu__items j-menu__button">
                                        {custormerPortal.name &&
                                            <Button
                                                color='light'
                                                size='md'
                                                className='btn__nobreak'
                                                rightIcon=''
                                                href={custormerPortal.link}
                                                target={custormerPortal.external}
                                                >
                                                {custormerPortal.name}
                                            </Button>
                                        }
                                        {signIn?.name &&
                                            <Button
                                                color='light'
                                                size='md'
                                                className='btn__nobreak'
                                                href={signIn.link}
                                                target={true}
                                                >
                                                {signIn.name}
                                                {signIn.icon && <span
                                                    className="btn__icon btn__icon--right"
                                                    dangerouslySetInnerHTML={{ __html: signIn.icon }}
                                                />}
                                            </Button>
                                        }
                                    </div>
                                }
                                <div className="c-md-8 j-menu__divider">

                                </div>
                                <div className="start-md-5 c-md-8 j-menu__clocks">
                                    {clocks.map((clock, i) => (
                                        <Clock
                                            key={i}
                                            background={clock.background}
                                            label={clock.label}
                                            zone={clock.zone}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search */}

                    <div id='j-menu__search' className={clsx('j-menu__search', isSearchActive && 'j-menu__search--active')}>
                        <div className='grid-container'>
                            <div className='grid-row j-menu__search-inner'>
                                <div className="c-12 j-menu__search-form">
                                    <SearchInput
                                        ref={searchInputRef} // Pass the ref to SearchInput
                                        className='j-menu__search-input'
                                        leftIcon='Search'
                                        rightIcon='ArrowRight'
                                        placeholder={searchText}
                                        href={searchHref}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

            </FocusTrap>
        </>
    );
}

